import { Chip, Grid, Icon, IconButton, Link } from '@material-ui/core';
import Badge from '@material-ui/core/Badge';
import {
  Assignment,
  AssignmentInd,
  AssignmentLate,
  AssignmentTurnedIn,
  Block,
  FindInPage,
  Save,
  AssignmentReturned,
} from '@material-ui/icons/';
import { createStyles, withStyles } from '@material-ui/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  DateInput,
  daysHoursMinutesFromNow,
  formatDate,
} from '../../core/date-time';
import {
  Flag,
  WorksheetState,
  WorksheetStateLabel,
  WorksheetSummary,
} from '../../core/domain';
import { withTooltip } from '../../core/format';
import { green, mediumGray, orange, red, yellow } from '../../core/theme';
import { transitionDialogueStore } from '../../core/transitions';
import { worksheetQueue } from '../../core/visit-queue';
import React from 'react';

export type WorksheetIconProps = Partial<WorksheetSummary> & {
  iconProps?: any;
  flags?: Flag[];
  showFlags?: boolean;
};

function statusColor(label: WorksheetStateLabel): string {
  switch (label) {
    case 'Audited':
    case 'Finalized':
    case 'Coded':
      return green;
    case 'Unassigned':
      return mediumGray;
    case 'Checked out for Review':
    case 'Checked Out':
      return orange;
    case 'Coding Review':
    case 'Returned to Coder':
    case 'Skipped Audit':
    case 'Force to Code Review':
    case 'Audit':
    case 'Stashed':
    case 'Sent to Provider':  
    case 'Returned to Provider':
    return yellow;
    case 'No Code':  
    case 'QA':
      return red;
    default:
      return mediumGray;
  }
}

export function StateIcon(props: WorksheetIconProps) {
  const { label } = props.stateInfo;
  const iconProps = { ...{ htmlColor: mediumGray }, ...props.iconProps };

  let htmlColor = statusColor(label);

  switch (label) {
    case 'Unassigned':
      return <Assignment {...iconProps} htmlColor={htmlColor} />;
    case 'Audited':
    case 'Finalized':
    case 'Coded':
      return <AssignmentTurnedIn {...iconProps} htmlColor={htmlColor} />;
    case 'Checked out for Review':
    case 'Checked Out':
      return <AssignmentInd {...iconProps} htmlColor={htmlColor} />;
    case 'Skipped Audit':
      return <AssignmentReturned {...iconProps} htmlColor={htmlColor} />;
    case 'Stashed':
      return <Save {...iconProps} htmlColor={htmlColor} />;
    case 'Returned to Coder':
    case 'Force to Code Review':
    case 'Audit':
    case 'Coding Review':
      return <FindInPage htmlColor={htmlColor} />;
    case 'No Code':
      return <Block htmlColor={htmlColor} />;
    case 'QA':
      return <AssignmentLate {...iconProps} htmlColor={htmlColor} />;
  }
  return <Assignment {...iconProps} htmlColor={htmlColor} />;
}

const badgeSize = 14;
const SingleLetterBadge: any = withStyles(() =>
  createStyles({
    badge: {
      right: 0,
      top: 0,
      fontSize: '8px',
      padding: '1px 0px 0px 0px',
      marginRight: -3,
      color: 'white',
      textAlign: 'right',
      width: badgeSize,
      minWidth: badgeSize,
      maxWidth: badgeSize,
      height: badgeSize,
      border: '1px solid white',
      backgroundColor: (props) =>
        (props && props.backgroundcolor) || mediumGray,
    },
  })
)(Badge);

export function WorksheetIcon(props: WorksheetIconProps) {

  if(!props){
    return '';
  }
  const { worksheetType, state, flags, showFlags } = props;

  

  const type = worksheetType.substring(0, 1);
  const { label } = props?.stateInfo || { label: '' };

  const onClick = () => {
    transitionDialogueStore.setState({
      open: true,
      summary: props as WorksheetSummary,
      transitionComplete: (s: WorksheetSummary) => {
        props.state = s.stateInfo.name as WorksheetState;
        props.stateInfo = s.stateInfo;
        worksheetQueue.getState().refreshRefs();
      },
    });
  };

  const color = statusColor(label as any);
  const flagsEnabled = !showFlags && flags?.length > 0;

  return (
    label?.length > 0 && (
      <Grid
        container
        direction="row"
        spacing={2}
        style={{ width: '100%', flexWrap: 'nowrap' }}
      >
        <Grid item>
          {withTooltip(
            state,
            <SingleLetterBadge
              badgeContent={type}
              backgroundcolor={color}
              onClick={onClick}
            >
              <StateIcon {...props} />
            </SingleLetterBadge>
          )}
        </Grid>

        <Grid
          item
          container
          direction="column"
          alignItems="center"
          justify="center"
          spacing={1}
          style={{ width: '100%', height: '100%', paddingTop: 12 }}
        >
          {flagsEnabled &&
            flags.map((v: Flag) =>
              withTooltip(
                v.description,
                <Grid item>
                  <div
                    style={{
                      color: 'white',
                      backgroundColor: v.resolved ? '#44aa44' : '#888',
                      textAlign: 'center',
                      borderRadius: 2,
                      paddingTop: 1,
                      margin: 1,
                      fontSize: 8,
                      width: 18,
                    }}
                  >
                    {v.code}
                  </div>
                </Grid>
              )
            )}
        </Grid>
      </Grid>
    )
  );
}

export function FacilityIcon(props: WorksheetIconProps) {

  if(!props){
    return '';
  }
  const { worksheetType, state, flags } = props;

  const type = worksheetType.substring(0, 1);
  const { label } = props?.stateInfo || { label: '' };

  const onClick = () => {
    transitionDialogueStore.setState({
      open: true,
      summary: props as WorksheetSummary,
      transitionComplete: (s: WorksheetSummary) => {
        props.state = s.stateInfo.name as WorksheetState;
        props.stateInfo = s.stateInfo;
        worksheetQueue.getState().refreshRefs();
      },
    });
  };

  const color = statusColor(label as any);

  return (
    label?.length > 0 && (
      <Grid
        container
        direction="row"
        spacing={2}
        style={{ width: '100%', flexWrap: 'nowrap' }}
      >
        <Grid item>
          {withTooltip(
            state,
            <SingleLetterBadge
              badgeContent={type}
              backgroundcolor={color}
              onClick={onClick}
            >
              <StateIcon {...props} />
            </SingleLetterBadge>
          )}
        </Grid>

        <Grid
          item
          container
          direction="column"
          alignItems="center"
          justify="center"
          spacing={1}
          style={{ width: '100%', height: '100%', paddingTop: 12 }}
        >
          {flags &&
            flags.map((v: Flag) =>
              withTooltip(
                v.description,
                <Grid item>
                  <div
                    style={{
                      color: 'white',
                      backgroundColor: v.resolved ? '#44aa44' : '#888',
                      textAlign: 'center',
                      borderRadius: 2,
                      paddingTop: 1,
                      margin: 1,
                      fontSize: 8,
                      width: 18,
                    }}
                  >
                    {v.code}
                  </div>
                </Grid>
              )
            )}
        </Grid>
      </Grid>
    )
  );
}


export function AgeInQueue(input: DateInput) {
  const label = daysHoursMinutesFromNow(input);
  return withTooltip(formatDate(input), <Chip label={label} />);
}

interface AccountLinkProps {
  route: string;
  icon: string;
  locationCode?: string;
  account?: string;
  target?: string;
  ref?: any;
  onClick?: (e?: any) => void;
}

export function AccountLink(props: AccountLinkProps) {
  const { route, locationCode, account, target, icon, onClick, ref } = props;
  const href = locationCode
    ? `${route}?locationCode=${locationCode}&account=${account}`
    : route;
  return (
    <Link
      onClick={onClick}
      component={RouterLink}
      target={target || null}
      to={href}
      style={{ textDecoration: 'none' }}
    >
      <IconButton color="primary">
        <Icon>{icon}</Icon>
      </IconButton>
    </Link>
  );
}
